import { ColumnState } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { toStoredColumnState } from '@/shared/components/ui/data-table/components/column-state-sync/column-state-sync.utils';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { useFilterDefaults } from '@/shared/components/ui/data-table/hooks/use-filter-params/filter-defaults-context';
import { PAGE_SIZE_QUERY_PARAM } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.constants';
import { useFilterScope } from '@/shared/features/filter-preferences/context/filter-scope-context';
import { useFilterPreferences } from '@/shared/features/filter-preferences/hooks/use-filter-preferences/use-filter-preferences';
import {
  TTableViewSettings,
  TUseTableViewSettings,
} from '@/shared/features/filter-preferences/hooks/use-table-view-settings/use-table-view-settings.types';
import {
  getToggleableColumns,
  toSavedFilters,
} from '@/shared/features/filter-preferences/hooks/use-table-view-settings/use-table-view-settings.utils';
import { useTableViewState } from '@/shared/features/filter-preferences/hooks/use-table-view-state/use-table-view-state';

export const useTableViewSettings = ({
  scopeKey,
  gridApi,
}: TUseTableViewSettings): TTableViewSettings => {
  const prefs = useFilterPreferences();
  const { unsavedParams } = useFilterScope();
  const { colDefs } = useDataTable();
  const { clearSnapshot, saveColumns } = useTableViewState(scopeKey);
  const filterDefaults = useFilterDefaults();
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnState, setColumnState] = useState<ColumnState[]>([]);

  // Visibility lives inside ag-grid: mirror it into state, or the menu keeps
  // showing the layout from before the last toggle.
  useEffect(() => {
    if (!gridApi) return;

    const sync = () => setColumnState(gridApi.getColumnState());
    sync();

    gridApi.addEventListener('columnVisible', sync);

    return () => {
      gridApi.removeEventListener('columnVisible', sync);
    };
  }, [gridApi]);

  const pageSize = Number(searchParams.get(PAGE_SIZE_QUERY_PARAM));

  const resetView = () => {
    clearSnapshot();
    // Reports source 'api', which the sync ignores, so the snapshot stays cleared.
    gridApi?.resetColumnState();
    // Filters fall back to their resolved defaults; dropping page, pageSize and
    // sort returns those to the code default.
    setSearchParams(new URLSearchParams(filterDefaults?.getDefaults() ?? {}));
  };

  const setColumnVisible = (colId: string, visible: boolean) => {
    if (!gridApi) return;
    gridApi.setColumnsVisible([colId], visible);
    // Also reports source 'api', indistinguishable from a restore, so persist here.
    saveColumns(toStoredColumnState(gridApi.getColumnState()));
  };

  return {
    hasStoredView: prefs.hasStoredView(scopeKey),
    columns: getToggleableColumns(colDefs, columnState),
    saveMine: async () => {
      await prefs.saveScopeView(scopeKey, {
        filters: toSavedFilters(searchParams, unsavedParams),
        ...(pageSize ? { pageSize } : {}),
        ...(gridApi
          ? { columns: toStoredColumnState(gridApi.getColumnState()) }
          : {}),
      });
      // The remembered view outranks the saved one, so it would keep winning.
      clearSnapshot();
    },
    removeMine: async () => {
      await prefs.removeScopeView(scopeKey);
      // The remembered view was seeded from the saved one, so it would restore it.
      clearSnapshot();
    },
    resetView,
    setColumnVisible,
  };
};
